/**
 * ===== DIMENSION VALIDATOR - VALIDATION DES DIMENSIONS =====
 * Validateur complet pour les dimensions et contraintes
 */
import { logger } from './ProfessionalLogger';

export interface DimensionConstraints {
  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
  maxHeight?: number;
  aspectRatio?: {
    min: number;
    max: number;
  };
  allowedRatios?: number[];
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  suggestions: string[];
}

export interface DimensionOptions {
  strictMode: boolean;
  allowZero: boolean;
  maxDimension: number;
  minDimension: number;
}

export class DimensionValidator {
  private static instance: DimensionValidator;
  private options: DimensionOptions;

  constructor(options: Partial<DimensionOptions> = {}) {
    this.options = {
      strictMode: false,
      allowZero: false,
      maxDimension: 10000,
      minDimension: 1,
      ...options,
    };
  }

  public static getInstance(): DimensionValidator {
    if (!DimensionValidator.instance) {
      DimensionValidator.instance = new DimensionValidator();
    }
    return DimensionValidator.instance;
  }

  /**
   * Valide des dimensions
   */
  public validateDimensions(
    width: number,
    height: number,
    constraints?: Partial<DimensionConstraints>
  ): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    const suggestions: string[] = [];

    try {
      // Validation de base
      this.validateBasicDimensions(width, height, errors, warnings);

      // Validation des contraintes si fournies
      if (constraints) {
        this.validateConstraints(
          width,
          height,
          constraints as DimensionConstraints,
          errors,
          warnings,
          suggestions
        );

        // Validation du ratio d'aspect
        if (constraints?.aspectRatio || constraints?.allowedRatios) {
          this.validateAspectRatioInternal(
            width,
            height,
            constraints as DimensionConstraints,
            warnings,
            suggestions
          );
        }
      }
    } catch (error) {
      logger.error('Erreur lors de la validation des dimensions:', error);
      errors.push('Erreur interne de validation');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      suggestions,
    };
  }

  /**
   * Valide une dimension spécifique
   */
  public validateDimension(
    value: number,
    type: 'width' | 'height',
    constraints?: Partial<DimensionConstraints>
  ): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    const suggestions: string[] = [];

    try {
      // Validation de base
      this.validateBasicDimension(value, type, errors, warnings);

      // Validation des contraintes spécifiques
      if (constraints) {
        this.validateSpecificConstraints(value, type, constraints, errors, warnings);
      }
    } catch (error) {
      logger.error('Erreur lors de la validation de dimension:', error);
      errors.push('Erreur interne de validation');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      suggestions,
    };
  }

  /**
   * Valide le ratio d'aspect
   */
  public validateAspectRatio(
    width: number,
    height: number,
    constraints?: DimensionConstraints
  ): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    const suggestions: string[] = [];

    try {
      if (width <= 0 || height <= 0) {
        return {
          isValid: false,
          errors: ['Les dimensions doivent être positives pour calculer le ratio'],
          warnings: [],
          suggestions: [],
        };
      }

      // Validation du ratio d'aspect
      this.validateAspectRatioInternal(width, height, constraints, warnings, suggestions);
    } catch (error) {
      logger.error('Erreur lors de la validation du ratio:', error);
      errors.push('Erreur interne de validation');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      suggestions,
    };
  }

  /**
   * Valide pour un format spécifique
   */
  public validateForFormat(
    width: number,
    height: number,
    format: 'social' | 'print' | 'web' | 'mobile' | 'video'
  ): ValidationResult {
    const formatConstraints = this.getFormatConstraints(format);
    return this.validateDimensions(width, height, formatConstraints);
  }

  /**
   * Obtient les contraintes pour un format
   */
  public getFormatConstraints(format: string): DimensionConstraints {
    const constraints: Record<string, DimensionConstraints> = {
      social: {
        minWidth: 400,
        maxWidth: 2048,
        minHeight: 400,
        maxHeight: 2048,
        aspectRatio: { min: 0.5, max: 2.0 },
        allowedRatios: [1, 1.91, 0.8, 1.33],
      },
      print: {
        minWidth: 300,
        maxWidth: 6000,
        minHeight: 300,
        maxHeight: 6000,
        aspectRatio: { min: 0.5, max: 3.0 },
      },
      web: {
        minWidth: 200,
        maxWidth: 4000,
        minHeight: 200,
        maxHeight: 4000,
        aspectRatio: { min: 0.3, max: 4.0 },
      },
      mobile: {
        minWidth: 200,
        maxWidth: 2000,
        minHeight: 200,
        maxHeight: 2000,
        aspectRatio: { min: 0.5, max: 2.0 },
      },
      video: {
        minWidth: 320,
        maxWidth: 7680,
        minHeight: 240,
        maxHeight: 4320,
        aspectRatio: { min: 0.5, max: 3.0 },
        allowedRatios: [1.33, 1.78, 2.35],
      },
    };
    return constraints[format] || constraints.web;
  }

  /**
   * Valide les dimensions de base
   */
  private validateBasicDimensions(
    width: number,
    height: number,
    errors: string[],
    warnings: string[]
  ): void {
    this.validateBasicDimension(width, 'width', errors, warnings);
    this.validateBasicDimension(height, 'height', errors, warnings);
  }

  /**
   * Valide une dimension de base
   */
  private validateBasicDimension(
    value: number,
    type: 'width' | 'height',
    errors: string[],
    warnings: string[]
  ): void {
    if (typeof value !== 'number' || isNaN(value)) {
      errors.push(`La ${type} doit être un nombre valide`);
      return;
    }
    if (value < 0) {
      errors.push(`La ${type} ne peut pas être négative`);
    }
    if (!this.options.allowZero && value === 0) {
      errors.push(`La ${type} ne peut pas être zéro`);
    }
    if (value < this.options.minDimension) {
      errors.push(`La ${type} est trop petite (minimum: ${this.options.minDimension})`);
    }
    if (value > this.options.maxDimension) {
      errors.push(`La ${type} est trop grande (maximum: ${this.options.maxDimension})`);
    }
    if (value > 0 && value < 10) {
      warnings.push(`La ${type} est très petite (${value}px)`);
    }
    if (value > 5000) {
      warnings.push(
        `La ${type} est très grande (${value}px) - cela peut affecter les performances`
      );
    }
  }

  /**
   * Valide les contraintes
   */
  private validateConstraints(
    width: number,
    height: number,
    constraints: DimensionConstraints,
    errors: string[],
    warnings: string[],
    suggestions: string[]
  ): void {
    // Validation des contraintes de largeur
    if (constraints.minWidth !== undefined && width < constraints.minWidth) {
      errors.push(
        `La largeur (${width}px) est inférieure au minimum requis (${constraints.minWidth}px)`
      );
    }
    if (constraints.maxWidth !== undefined && width > constraints.maxWidth) {
      errors.push(
        `La largeur (${width}px) dépasse le maximum autorisé (${constraints.maxWidth}px)`
      );
    }

    // Validation des contraintes de hauteur
    if (constraints.minHeight !== undefined && height < constraints.minHeight) {
      errors.push(
        `La hauteur (${height}px) est inférieure au minimum requis (${constraints.minHeight}px)`
      );
    }
    if (constraints.maxHeight !== undefined && height > constraints.maxHeight) {
      errors.push(
        `La hauteur (${height}px) dépasse le maximum autorisé (${constraints.maxHeight}px)`
      );
    }

    // Validation du ratio d'aspect
    if (constraints.aspectRatio && width > 0 && height > 0) {
      const aspectRatio = width / height;
      if (aspectRatio < constraints.aspectRatio.min) {
        errors.push(
          `Le ratio d'aspect (${aspectRatio.toFixed(2)}) est inférieur au minimum (${constraints.aspectRatio.min})`
        );
      }
      if (aspectRatio > constraints.aspectRatio.max) {
        errors.push(
          `Le ratio d'aspect (${aspectRatio.toFixed(2)}) dépasse le maximum (${constraints.aspectRatio.max})`
        );
      }
    }

    // Validation des ratios autorisés
    if (
      constraints.allowedRatios &&
      constraints.allowedRatios.length > 0 &&
      width > 0 &&
      height > 0
    ) {
      const aspectRatio = width / height;
      const closestRatio = this.findClosestRatio(aspectRatio, constraints.allowedRatios);
      if (Math.abs(aspectRatio - closestRatio) > 0.1) {
        suggestions.push(
          `Ratio d'aspect recommandé: ${closestRatio.toFixed(2)} (actuel: ${aspectRatio.toFixed(2)})`
        );
      }
    }
  }

  /**
   * Valide le ratio d'aspect (interne)
   */
  private validateAspectRatioInternal(
    width: number,
    height: number,
    constraints: DimensionConstraints | undefined,
    warnings: string[],
    suggestions: string[]
  ): void {
    if (width <= 0 || height <= 0) return;

    const aspectRatio = width / height;

    if (constraints?.aspectRatio) {
      if (aspectRatio < constraints.aspectRatio.min) {
        warnings.push(
          `Ratio d'aspect faible: ${aspectRatio.toFixed(2)} (recommandé: >= ${constraints.aspectRatio.min})`
        );
      }
      if (aspectRatio > constraints.aspectRatio.max) {
        warnings.push(
          `Ratio d'aspect élevé: ${aspectRatio.toFixed(2)} (recommandé: <= ${constraints.aspectRatio.max})`
        );
      }
    }

    // Génération de suggestions pour les ratios courants
    this.generateAspectRatioSuggestions(aspectRatio, suggestions);
  }

  /**
   * Valide les contraintes spécifiques
   */
  private validateSpecificConstraints(
    value: number,
    type: 'width' | 'height',
    constraints: Partial<DimensionConstraints>,
    errors: string[],
    _warnings: string[]
  ): void {
    const minKey = type === 'width' ? 'minWidth' : 'minHeight';
    const maxKey = type === 'width' ? 'maxWidth' : 'maxHeight';
    const minValue = constraints[minKey];
    const maxValue = constraints[maxKey];

    if (minValue !== undefined && value < minValue) {
      errors.push(`La ${type} (${value}px) est inférieure au minimum (${minValue}px)`);
    }
    if (maxValue !== undefined && value > maxValue) {
      errors.push(`La ${type} (${value}px) dépasse le maximum (${maxValue}px)`);
    }
  }

  /**
   * Trouve le ratio le plus proche
   */
  private findClosestRatio(aspectRatio: number, allowedRatios: number[]): number {
    return allowedRatios.reduce((closest, ratio) => {
      return Math.abs(aspectRatio - ratio) < Math.abs(aspectRatio - closest) ? ratio : closest;
    });
  }

  /**
   * Génère des suggestions pour les ratios d'aspect
   */
  private generateAspectRatioSuggestions(aspectRatio: number, suggestions: string[]): void {
    if (Math.abs(aspectRatio - 1) < 0.1) {
      suggestions.push('Ratio carré détecté - parfait pour les profils sociaux');
    } else if (Math.abs(aspectRatio - 1.78) < 0.1) {
      suggestions.push('Ratio 16:9 détecté - idéal pour les vidéos et présentations');
    } else if (Math.abs(aspectRatio - 1.33) < 0.1) {
      suggestions.push('Ratio 4:3 détecté - bon pour les photos traditionnelles');
    } else if (Math.abs(aspectRatio - 0.8) < 0.1) {
      suggestions.push('Ratio portrait détecté - parfait pour les stories Instagram');
    } else {
      suggestions.push(
        `Ratio personnalisé: ${aspectRatio.toFixed(2)} - vérifiez la compatibilité avec votre plateforme`
      );
    }
  }

  /**
   * Configure les options
   */
  public configure(options: Partial<DimensionOptions>): void {
    this.options = { ...this.options, ...options };
  }

  /**
   * Obtient les options actuelles
   */
  public getOptions(): DimensionOptions {
    return { ...this.options };
  }

  /**
   * Nettoie les ressources
   */
  public destroy(): void {
    // Nettoyage si nécessaire
  }
}
